/**
 * Export Panel Component
 *
 * Reusable drawer panel for exporting table data
 * Features:
 * - Format selection (Excel, PDF, CSV)
 * - Column selection with checkboxes (disabled for CSV - exports all columns)
 * - Preview file name with timestamp
 * - Export and Cancel buttons
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Radio, Checkbox, Button, message } from 'antd';
import type { RadioChangeEvent } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import RightContentDrawer from './RightContentDrawer';
import ButtonGroup from './ButtonGroup';
import { type ExportFormat } from '../../utils/exportUtils';
import { exportDashboardChartsToPdf } from '@/utils/exportDashboardChartsToPdf';
import type { RootState } from '@/store';

// ========== TYPES ==========

export interface ExportColumnConfig {
  key: string;
  title: string;
  width?: number;
  defaultSelected?: boolean;
}

export interface DashboardExportPanelProps<T extends Record<string, unknown>> {
  open: boolean;
  onClose: () => void;
  data: T[];
  charts: ExportColumnConfig[];
  fileName: string;
  title?: string;
  sheetName?: string;
}

// ========== STYLED COMPONENTS ==========

const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div``;

const SectionTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1f2937;
  margin-bottom: 12px;
`;

const FormatGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormatOption = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid ${props => (props.$selected ? '#2563EB' : '#E5E7EB')};
  border-radius: 8px;
  background: ${props => (props.$selected ? '#EFF6FF' : '#FFFFFF')};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #2563eb;
  }

  .ant-radio-wrapper {
    width: 100%;
    margin: 0;
  }
`;

const FormatLabel = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1f2937;
  margin-left: 8px;
`;

const FormatExt = styled.span`
  font-family: Inter;
  font-size: 12px;
  color: #6b7280;
  margin-left: 4px;
`;

const ChartSection = styled.div<{ $disabled?: boolean }>`
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  pointer-events: ${props => (props.$disabled ? 'none' : 'auto')};
`;

const ChartList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
`;

const ChartItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;

  &:hover {
    background: #f9fafb;
  }

  .ant-checkbox-wrapper {
    width: 100%;

    span:last-child {
      font-family: Inter;
      font-size: 14px;
      color: #1f2937;
    }
  }
`;

const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 4px;

  .ant-checkbox-wrapper {
    span:last-child {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #1f2937;
    }
  }
`;

const SelectedCount = styled.span`
  font-family: Inter;
  font-size: 12px;
  color: #6b7280;
`;

const FilePreview = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
`;

const FilePreviewLabel = styled.div`
  font-family: Inter;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const FilePreviewName = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1f2937;
  word-break: break-all;
`;

const FooterButtonGroup = styled(ButtonGroup)`
  margin-bottom: 0;

  .ant-btn {
    flex: 1;
    height: 44px;
  }
`;

// ========== COMPONENT ==========

function DashboardExportPanel<T extends Record<string, unknown>>({
  open,
  onClose,
  data: _data,
  charts,
  fileName,
  title: _title,
  sheetName: _sheetName,
}: DashboardExportPanelProps<T>): React.ReactElement {
  const { user } = useSelector((state: RootState) => state.auth);

  // State
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [selectedCharts, setSelectedCharts] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  // Initialize selected charts when panel opens
  useEffect(() => {
    if (open) {
      const defaultSelected = charts
        .filter(chart => chart.defaultSelected !== false)
        .map(chart => chart.key);
      setSelectedCharts(defaultSelected);
      setFormat('pdf');
    }
  }, [open, charts]);

  // Get file extension based on format
  const getExtension = useCallback((fmt: ExportFormat): string => {
    switch (fmt) {
      case 'excel':
        return '.xlsx';
      case 'pdf':
        return '.pdf';
      case 'csv':
        return '.csv';
      default:
        return '';
    }
  }, []);

  // Preview file name with timestamp
  const previewFileName = useMemo(() => {
    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:-]/g, '')
      .replace('T', '_');
    return `${fileName}_${timestamp}${getExtension(format)}`;
  }, [fileName, format, getExtension]);

  // Handle format change
  const handleFormatChange = useCallback((e: RadioChangeEvent) => {
    setFormat(e.target.value as ExportFormat);
  }, []);

  // Handle chart toggle
  const handleChartToggle = useCallback((key: string, checked: boolean) => {
    setSelectedCharts(prev =>
      checked ? [...prev, key] : prev.filter(k => k !== key),
    );
  }, []);

  // Handle select all
  const handleSelectAll = useCallback(
    (e: CheckboxChangeEvent) => {
      if (e.target.checked) {
        setSelectedCharts(charts.map(chart => chart.key));
      } else {
        setSelectedCharts([]);
      }
    },
    [charts],
  );

  // Check if all selected
  const isAllSelected = selectedCharts.length === charts.length;
  const isIndeterminate =
    selectedCharts.length > 0 && selectedCharts.length < charts.length;

  const companyName = localStorage.getItem("companyName");
  const companyImageUrl = localStorage.getItem("companyImageUrl");

  // Handle export
  const handleExport = useCallback(async () => {
    if (selectedCharts.length === 0) {
      message.warning('Please select at least one chart to export');
      return;
    }

    setExporting(true);
    try {
      // Get selected column configs
      const items = charts
        .filter(chart => selectedCharts.includes(chart.key))
        .map(chart => ({ key: chart.key, title: chart.title }));

      await exportDashboardChartsToPdf({
        items,
        fileNameBase: fileName, // "Dashboard_Report_"
        pageOrientation: 'p',
        companyLogoUrl: companyImageUrl,
        companyName: companyName,
      });

      message.success(
        `Exported ${items.length} charts to ${format.toUpperCase()}`,
      );
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      message.error('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [selectedCharts, charts, format, fileName, user, onClose]);

  // Footer - following FinancialStatementItemDrawer button style
  const footer = (
    <FooterButtonGroup>
      <Button type='default' onClick={onClose} disabled={exporting}>
        Go back
      </Button>
      <Button
        type='primary'
        icon={<DownloadOutlined />}
        onClick={handleExport}
        loading={exporting}
        disabled={selectedCharts.length === 0}
      >
        Export
      </Button>
    </FooterButtonGroup>
  );

  return (
    <RightContentDrawer
      open={open}
      title='Export Data'
      onClose={onClose}
      width={500}
      footer={footer}
      resizable={true}
    >
      <PanelContent>
        {/* File Format Selection */}
        <Section>
          <SectionTitle>File Format</SectionTitle>
          <Radio.Group
            value={format}
            onChange={handleFormatChange}
            style={{ width: '100%' }}
          >
            <FormatGroup>
              <FormatOption $selected={format === 'pdf'}>
                <Radio value='pdf'>
                  <FormatLabel>PDF</FormatLabel>
                  <FormatExt>(.pdf)</FormatExt>
                </Radio>
              </FormatOption>
            </FormatGroup>
          </Radio.Group>
        </Section>

        {/* Column Selection */}
        <Section>
          <SectionTitle>Select Charts</SectionTitle>
          <ChartSection>
            <ChartList>
              <SelectAllRow>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  disabled={false}
                >
                  Select All
                </Checkbox>
                <SelectedCount>
                  {selectedCharts.length} of {charts.length} selected
                </SelectedCount>
              </SelectAllRow>
              {charts.map(chart => (
                <ChartItem key={chart.key}>
                  <Checkbox
                    checked={selectedCharts.includes(chart.key)}
                    onChange={(e: CheckboxChangeEvent) =>
                      handleChartToggle(chart.key, e.target.checked)
                    }
                    disabled={false}
                  >
                    {chart.title}
                  </Checkbox>
                </ChartItem>
              ))}
            </ChartList>
          </ChartSection>
        </Section>

        {/* File Preview */}
        <Section>
          <SectionTitle>File Preview</SectionTitle>
          <FilePreview>
            <FilePreviewLabel>File name</FilePreviewLabel>
            <FilePreviewName>{previewFileName}</FilePreviewName>
          </FilePreview>
        </Section>
      </PanelContent>
    </RightContentDrawer>
  );
}

export default DashboardExportPanel;
